import { isStoreAnnouncement } from './flyerFields';
import {
  fetchFlyerResource,
  fetchFlyerWithCustomLabelResource,
  fetchNextFlyerAvailabilityResource,
} from 'resources';

/**
 * Dual-format flyer logic for Smart Viewer (retailer-agnostic).
 *
 * PDF = classic multi-page viewer; NEXT = interactive flyer (`store-announcement` / click-out).
 *
 * `promo_code` + `_next` labels are probed via customResources. **Dual CTA** only when both
 * distinct ids resolve (merged under {@link SF_VIEWER_VERSION_LINKS_KEY}). Partial probe results
 * feed {@link pickFlyerIdForDefaultVersion} for init fallbacks.
 *
 * A NEXT id from customResources counts only when store-announcement-format confirms the flyer is
 * published ({@link fetchNextFlyerAvailabilityResource}); otherwise it is treated as absent.
 *
 * Once customResources returns **no flyer id** for a label (fulfilled empty/minimal), we cache that
 * under {@link SF_VIEWER_PDF_UNAVAILABLE_KEY} / {@link SF_VIEWER_NEXT_UNAVAILABLE_KEY} so we **do not**
 * request that label again (AC: no repeat NEXT/PDF probe after absence is confirmed).
 */

/** Client-only: both PDF + NEXT ids — enables switch CTA (not written for single-format probes). */
export const SF_VIEWER_VERSION_LINKS_KEY = '_sfViewerVersionLinks';

/** Client-only: PDF promo label returned no usable flyer id — skip repeat `customResources` PDF fetch. */
export const SF_VIEWER_PDF_UNAVAILABLE_KEY = '_sfViewerPdfUnavailable';

/** Client-only: NEXT promo label (`*_next`) returned no usable flyer id — skip repeat NEXT fetch. */
export const SF_VIEWER_NEXT_UNAVAILABLE_KEY = '_sfViewerNextUnavailable';

const getResolvedFlyerId = (result) => {
  if (result?.status !== 'fulfilled') return null;
  const id = result.value?.id;
  return typeof id === 'string' && id ? id : null;
};

const fulfilledWithoutFlyerId = (result) =>
  result.status === 'fulfilled' && !getResolvedFlyerId(result);

const getNextLandingUrl = (flyer) => flyer?.settings?.spotlight?.[0]?.landing_url;

/**
 * NEXT flyer from customResources → is its page live on store-announcement-format?
 * The customResources payload may not carry `landing_url`: read it from the full NEXT flyer then.
 */
const isNextFlyerPublished = async (nextFlyer, storeId) => {
  let landingUrl = getNextLandingUrl(nextFlyer);
  if (!landingUrl) {
    try {
      const fullNextFlyer = await fetchFlyerResource({
        flyerId: nextFlyer.id,
        storeCustomLabel: storeId,
      });
      landingUrl = getNextLandingUrl(fullNextFlyer);
    } catch {
      landingUrl = null;
    }
  }
  return fetchNextFlyerAvailabilityResource(landingUrl);
};

/** `history.location.state` key: saga skips `defaultFlyerVersion` realignment (e.g. PDF↔NEXT CTA). */
export const SKIP_DEFAULT_FLYER_VERSION_ALIGN_STATE_KEY = 'skipDefaultFlyerVersionAlign';

export const createSkipDefaultFlyerVersionAlignState = () => ({
  [SKIP_DEFAULT_FLYER_VERSION_ALIGN_STATE_KEY]: true,
});

/** Suffix for the NEXT-format flyer’s `promo_code` vs the classic PDF row (same base code). */
export const PROMO_NEXT_SUFFIX = '_next';
const PROMO_NEXT_SUFFIX_LOWER = PROMO_NEXT_SUFFIX.toLowerCase();

const hasPromoNextSuffix = (promo) =>
  typeof promo === 'string' && promo.toLowerCase().endsWith(PROMO_NEXT_SUFFIX_LOWER);

const buildVersionLinks = (PDF, NEXT) =>
  PDF || NEXT
    ? {
        ...(typeof PDF === 'string' && PDF ? { PDF } : {}),
        ...(typeof NEXT === 'string' && NEXT ? { NEXT } : {}),
      }
    : null;

const getUnavailableFlagsPatchFromExtras = (extras) => {
  const patch = {};
  if (extras?.[SF_VIEWER_PDF_UNAVAILABLE_KEY]) patch[SF_VIEWER_PDF_UNAVAILABLE_KEY] = true;
  if (extras?.[SF_VIEWER_NEXT_UNAVAILABLE_KEY]) patch[SF_VIEWER_NEXT_UNAVAILABLE_KEY] = true;
  return patch;
};

const getSeededVersionIds = (flyer) => {
  let PDF;
  let NEXT;
  const currentFlyerVersion = getFlyerVersionType(flyer);
  if (flyer?.id && typeof flyer.id === 'string') {
    if (currentFlyerVersion === FLYER_VERSION_PDF) PDF = flyer.id;
    else NEXT = flyer.id;
  }
  return { PDF, NEXT, currentFlyerVersion };
};

/**
 * Base promo/custom label shared by both formats (strips trailing `_next` when present).
 * @returns {string|null}
 */
export const getPromoBaseCode = (promo) => {
  if (!promo || typeof promo !== 'string') return null;
  return hasPromoNextSuffix(promo) ? promo.slice(0, -PROMO_NEXT_SUFFIX.length) : promo;
};

/**
 * Labels to resolve PDF vs NEXT flyer ids through `customResources/flyer/{label}`.
 * @returns {{ pdfLabel: string, nextLabel: string }|null}
 */
export const getPromoDualLabels = (promo) => {
  const base = getPromoBaseCode(promo);
  if (!base) return null;
  return { pdfLabel: base, nextLabel: `${base}${PROMO_NEXT_SUFFIX}` };
};

export const FLYER_VERSION_PDF = 'PDF';
export const FLYER_VERSION_NEXT = 'NEXT';

/**
 * Format of the loaded flyer for dual-viewer UX.
 * NEXT only when {@link isStoreAnnouncement}; otherwise PDF (classic viewer) — i.e. PDF ≡ ¬store-announcement.
 * @returns {'PDF'|'NEXT'}
 */
export const getFlyerVersionType = (flyer) =>
  isStoreAnnouncement(flyer) ? FLYER_VERSION_NEXT : FLYER_VERSION_PDF;

/**
 * Resolved PDF/NEXT flyer ids (from promo + customResources), client cache only — **dual pair only**.
 * @returns {{ PDF?: string, NEXT?: string }|null}
 */
export const getFlyerVersionLinks = (flyer) => {
  const links = flyer?.extras?.[SF_VIEWER_VERSION_LINKS_KEY];
  if (!links || typeof links !== 'object') return null;
  const { PDF, NEXT } = links;
  const out = {};
  if (typeof PDF === 'string' && PDF) out.PDF = PDF;
  if (typeof NEXT === 'string' && NEXT) out.NEXT = NEXT;
  return Object.keys(out).length ? out : null;
};

export const hasDualFlyerVersions = (links) =>
  Boolean(links?.PDF && links?.NEXT && links.PDF !== links.NEXT);

/**
 * Merges resolved PDF/NEXT ids into the client-only {@link SF_VIEWER_VERSION_LINKS_KEY} field.
 */
export const mergeVersionLinksIntoFlyer = (flyer, links) => {
  if (!links || typeof links !== 'object') return flyer;
  const keys = Object.keys(links);
  if (!keys.length) return flyer;
  return {
    ...flyer,
    extras: {
      ...flyer.extras,
      [SF_VIEWER_VERSION_LINKS_KEY]: {
        ...flyer.extras?.[SF_VIEWER_VERSION_LINKS_KEY],
        ...links,
      },
    },
  };
};

/** After {@link mergeFlyerVersionResolution}: links for {@link pickFlyerIdForDefaultVersion} (dual in extras or partial from last probe). */
export const getVersionLinksAfterResolution = (flyer, resolution) =>
  getFlyerVersionLinks(flyer) ?? resolution?.versionLinks ?? null;

/**
 * After PDF ↔ NEXT navigation, the flyer payload from the API often lacks client-only fields.
 * When both flyers share the same promo base, reuse links + absence flags from the prior viewer row.
 */
export const copyClientVersionExtrasFromPriorIfSamePromo = (priorFlyer, fetchedFlyer) => {
  if (!priorFlyer?.extras || !fetchedFlyer) return fetchedFlyer;
  const basePrior = getPromoBaseCode(priorFlyer.extras?.promo_code);
  const baseFetched = getPromoBaseCode(fetchedFlyer.extras?.promo_code);
  if (!basePrior || !baseFetched || basePrior !== baseFetched) return fetchedFlyer;
  const src = priorFlyer.extras;
  const patch = getUnavailableFlagsPatchFromExtras(src);
  const links = src[SF_VIEWER_VERSION_LINKS_KEY];
  if (links && typeof links === 'object' && Object.keys(links).length) {
    patch[SF_VIEWER_VERSION_LINKS_KEY] = { ...links };
  }
  if (!Object.keys(patch).length) return fetchedFlyer;
  return {
    ...fetchedFlyer,
    extras: { ...(fetchedFlyer.extras || {}), ...patch },
  };
};

/**
 * NEXT format failed to load in store-announcement-format: drop the NEXT link (so the switch CTA
 * hides) and flag it unavailable (so we do not probe/restore it on the PDF view).
 */
export const applyNextVersionUnavailable = (flyer, pdfId) => {
  if (!flyer) return flyer;
  const resolvedPdfId = pdfId || getFlyerVersionLinks(flyer)?.PDF;
  return {
    ...flyer,
    extras: {
      ...(flyer.extras || {}),
      ...(typeof resolvedPdfId === 'string' && resolvedPdfId
        ? { [SF_VIEWER_VERSION_LINKS_KEY]: { PDF: resolvedPdfId } }
        : {}),
      [SF_VIEWER_NEXT_UNAVAILABLE_KEY]: true,
    },
  };
};

/** Copies PDF/NEXT probe-absence flags onto another flyer so we do not re-fetch dismissed labels. */
export const copyVersionUnavailableFlagsOntoFlyer = (fromFlyer, toFlyer) => {
  if (!fromFlyer?.extras || !toFlyer) return toFlyer;
  const patch = getUnavailableFlagsPatchFromExtras(fromFlyer.extras);
  if (!Object.keys(patch).length) return toFlyer;
  return {
    ...toFlyer,
    extras: { ...toFlyer.extras, ...patch },
  };
};

/** Applies cached dual links + unavailable flags from a resolved source flyer onto a re-fetched target. */
export const mergeClientVersionLinksAndFlagsOntoFlyer = (sourceFlyer, targetFlyer) => {
  if (!sourceFlyer || !targetFlyer) return targetFlyer;
  const withLinks = mergeVersionLinksIntoFlyer(targetFlyer, getFlyerVersionLinks(sourceFlyer));
  return copyVersionUnavailableFlagsOntoFlyer(sourceFlyer, withLinks);
};

/** Merges dual pair and/or absence flags into extras after {@link resolveFlyerVersionLinks}. */
export const mergeFlyerVersionResolution = (flyer, resolution) => {
  if (!resolution) return flyer;
  let next = flyer;
  const extrasPatch = {};
  if (resolution.pdfUnavailable) extrasPatch[SF_VIEWER_PDF_UNAVAILABLE_KEY] = true;
  if (resolution.nextUnavailable) extrasPatch[SF_VIEWER_NEXT_UNAVAILABLE_KEY] = true;
  if (resolution.versionLinks && hasDualFlyerVersions(resolution.versionLinks)) {
    next = mergeVersionLinksIntoFlyer(next, resolution.versionLinks);
  }
  if (Object.keys(extrasPatch).length) {
    next = { ...next, extras: { ...(next.extras || {}), ...extrasPatch } };
  }
  return next;
};

/**
 * @returns {{
 *   versionLinks: { PDF?: string, NEXT?: string }|null,
 *   pdfUnavailable?: boolean,
 *   nextUnavailable?: boolean,
 * }}
 */
export const resolveFlyerVersionLinks = async (flyer, storeId, retailerId) => {
  const cachedDual = getFlyerVersionLinks(flyer);
  if (hasDualFlyerVersions(cachedDual)) {
    return {
      versionLinks: cachedDual,
    };
  }

  const dual = getPromoDualLabels(flyer?.extras?.promo_code);
  if (!dual) {
    return { versionLinks: null };
  }

  let { PDF, NEXT, currentFlyerVersion } = getSeededVersionIds(flyer);

  // Legacy NEXT rows may use base promo_code (without `_next`) and have no paired PDF row.
  // In that scenario, skip PDF probe entirely to avoid useless customResources calls.
  const isLegacyStandaloneNext =
    currentFlyerVersion === FLYER_VERSION_NEXT &&
    typeof flyer?.extras?.promo_code === 'string' &&
    !hasPromoNextSuffix(flyer.extras.promo_code);

  const pdfUnavailableCached = flyer?.extras?.[SF_VIEWER_PDF_UNAVAILABLE_KEY] === true;
  const nextUnavailableCached = flyer?.extras?.[SF_VIEWER_NEXT_UNAVAILABLE_KEY] === true;

  const shouldFetchPdf = !PDF && !pdfUnavailableCached && !isLegacyStandaloneNext;
  const shouldFetchNext = !NEXT && !nextUnavailableCached;

  if (!shouldFetchPdf && !shouldFetchNext) {
    const versionLinks = buildVersionLinks(PDF, NEXT);
    const out = { versionLinks };
    if (isLegacyStandaloneNext) out.pdfUnavailable = true;
    return out;
  }

  // proxy strips retailer_id; nested retailer.id is the field on the flyer payload
  const probeRetailerId = flyer?.retailer?.id ?? retailerId ?? null;

  const [pdfResult, nextResult] = await Promise.allSettled([
    shouldFetchPdf
      ? fetchFlyerWithCustomLabelResource(dual.pdfLabel, storeId, probeRetailerId)
      : Promise.resolve(null),
    shouldFetchNext
      ? fetchFlyerWithCustomLabelResource(dual.nextLabel, storeId, probeRetailerId)
      : Promise.resolve(null),
  ]);

  let pdfUnavailable = false;
  let nextUnavailable = false;

  if (shouldFetchPdf && fulfilledWithoutFlyerId(pdfResult)) pdfUnavailable = true;

  if (shouldFetchNext && fulfilledWithoutFlyerId(nextResult)) nextUnavailable = true;

  if (shouldFetchPdf) {
    const resolvedPdfId = getResolvedFlyerId(pdfResult);

    // If the "PDF label" resolves to the known NEXT id, treat it as non-dual/missing PDF.
    if (resolvedPdfId) {
      if (NEXT && resolvedPdfId === NEXT) {
        pdfUnavailable = true;
      } else {
        PDF = resolvedPdfId;
        console.log('[sfViewer:flyerVersion] PDF format id from customResources', {
          label: dual.pdfLabel,
          flyerId: PDF,
        });
      }
    }
  }

  if (shouldFetchNext) {
    const resolvedNextId = getResolvedFlyerId(nextResult);

    // If the "NEXT label" resolves to the known PDF id, treat it as non-dual/missing NEXT.
    if (resolvedNextId) {
      if (PDF && resolvedNextId === PDF) {
        nextUnavailable = true;
      } else if (!(await isNextFlyerPublished(nextResult.value, storeId))) {
        // The NEXT flyer exists on the API side but the NEXT format does not confirm it is
        // published: keep it out of the links so the PDF/NEXT switch is not shown.
        nextUnavailable = true;
        console.log('[sfViewer:flyerVersion] NEXT format not published or not verifiable', {
          label: dual.nextLabel,
          flyerId: resolvedNextId,
        });
      } else {
        NEXT = resolvedNextId;
        console.log('[sfViewer:flyerVersion] NEXT format id from customResources', {
          label: dual.nextLabel,
          flyerId: NEXT,
        });
      }
    }
  }

  const versionLinks = buildVersionLinks(PDF, NEXT);

  const out = {
    versionLinks,
  };
  if (isLegacyStandaloneNext) out.pdfUnavailable = true;
  if (pdfUnavailable) out.pdfUnavailable = true;
  if (nextUnavailable) out.nextUnavailable = true;
  return out;
};

export const getTargetFlyerIdForDefaultVersion = ({
  flyer,
  resolution,
  defaultFlyerVersion,
  skipDefaultAlign = false,
}) => {
  if (skipDefaultAlign) return null;
  if (defaultFlyerVersion !== FLYER_VERSION_PDF && defaultFlyerVersion !== FLYER_VERSION_NEXT) {
    return null;
  }
  return pickFlyerIdForDefaultVersion(
    getVersionLinksAfterResolution(flyer, resolution),
    flyer?.id,
    defaultFlyerVersion,
  );
};

/**
 * Flyer id that matches Smart Viewer init `defaultFlyerVersion` when PDF/NEXT ids are known (dual or single probe).
 * Falls back to the other format’s id when the preferred one is missing.
 */
export const pickFlyerIdForDefaultVersion = (links, currentFlyerId, defaultVersion) => {
  if (!links) return null;
  if (defaultVersion !== FLYER_VERSION_PDF && defaultVersion !== FLYER_VERSION_NEXT) return null;

  const preferred = defaultVersion === FLYER_VERSION_NEXT ? links.NEXT : links.PDF;
  const alternate = defaultVersion === FLYER_VERSION_NEXT ? links.PDF : links.NEXT;
  const chosen = preferred || alternate;
  if (!chosen) return null;
  if (currentFlyerId && chosen === currentFlyerId) return null;
  return chosen;
};

/**
 * The linked id for the format we are *not* showing (CTA target). Requires merged dual links only.
 * @returns {string|null}
 */
export const getAlternateVersionFlyerId = (flyer) => {
  const links = getFlyerVersionLinks(flyer);
  if (!hasDualFlyerVersions(links)) return null;
  const t = getFlyerVersionType(flyer);
  return t === FLYER_VERSION_PDF ? links.NEXT : links.PDF;
};
